import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  MapPin,
  Bed,
  Bath,
  Maximize,
  Square,
  Phone,
  Mail,
  ArrowLeft,
  Heart,
  Share2,
} from 'lucide-react';
import { motion } from 'framer-motion';
import axiosInstance from '@/utils/axios';
import PropertyCard from '@/components/PropertyCard';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import {
  formatPrice,
  formatArea,
  getPropertyTypeLabel,
  getOperationLabel,
} from '@/utils/formatters';
import { toast } from 'sonner';

const PropertyDetailPage = () => {
  const { slug, id } = useParams(); // Support both slug and legacy id routes
  const slugOrId = slug || id;
  const [property, setProperty] = useState(null);
  const [relatedProperties, setRelatedProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPropertyDetail();
  }, [slugOrId]);

  const fetchPropertyDetail = async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get(`/properties/${slugOrId}`);
      setProperty(data);

      // Fetch related properties
      const { data: relatedData } = await axiosInstance.get('/properties', {
        params: {
          city: data.city,
          operation: data.operation,
          limit: 3,
        },
      });
      const related = relatedData.properties
        .filter((p) => p.id !== data.id)
        .slice(0, 3);
      setRelatedProperties(related);
    } catch (error) {
      console.error('Error fetching property:', error);
      toast.error('Error al cargar la propiedad');
    } finally {
      setLoading(false);
    }
  };

  const handleContact = (method) => {
    toast.success(`Contacto por ${method} iniciado`);
  };

  if (loading) {
    return (
      <div className='min-h-screen bg-slate-50 py-8'>
        <div className='max-w-7xl mx-auto px-4 md:px-8'>
          <div className='animate-pulse'>
            <div className='h-96 bg-slate-200 rounded-2xl mb-8' />
            <div className='h-8 bg-slate-200 rounded w-1/2 mb-4' />
            <div className='h-6 bg-slate-200 rounded w-1/4 mb-8' />
          </div>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className='min-h-screen bg-slate-50 py-16 flex items-center justify-center'>
        <div className='text-center'>
          <h2 className='text-2xl font-heading font-bold text-slate-900 mb-4'>
            Propiedad no encontrada
          </h2>
          <Link to='/buscar'>
            <Button>Volver a la búsqueda</Button>
          </Link>
        </div>
      </div>
    );
  }

  const features = [
    {
      label: 'Tipo',
      value: getPropertyTypeLabel(property.property_type),
      icon: Square,
    },
    { label: 'Área', value: formatArea(property.area_m2), icon: Maximize },
    ...(property.rooms
      ? [{ label: 'Habitaciones', value: property.rooms, icon: Bed }]
      : []),
    ...(property.bathrooms
      ? [{ label: 'Baños', value: property.bathrooms, icon: Bath }]
      : []),
    ...(property.parking
      ? [{ label: 'Parqueaderos', value: property.parking, icon: Square }]
      : []),
  ];

  return (
    <div className='min-h-screen bg-slate-50 py-8'>
      <div className='max-w-7xl mx-auto px-4 md:px-8'>
        {/* Back Button */}
        <Link
          to='/buscar'
          className='inline-flex items-center text-slate-600 hover:text-primary mb-6'
          data-testid='back-button'
        >
          <ArrowLeft className='w-4 h-4 mr-2' />
          Volver a resultados
        </Link>

        <div className='grid grid-cols-1 lg:grid-cols-3 gap-8'>
          {/* Main Content */}
          <div className='lg:col-span-2'>
            {/* Image Gallery */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className='mb-8'
              data-testid='property-gallery'
            >
              {property.images.length > 0 ? (
                <Carousel className='w-full'>
                  <CarouselContent>
                    {property.images.map((image, index) => (
                      <CarouselItem key={index}>
                        <div className='relative h-96 md:h-[500px] rounded-2xl overflow-hidden'>
                          <img
                            src={image}
                            alt={`${property.title} - Imagen ${index + 1}`}
                            className='w-full h-full object-cover'
                          />
                        </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  <CarouselPrevious className='left-4' />
                  <CarouselNext className='right-4' />
                </Carousel>
              ) : (
                <div className='h-96 md:h-[500px] bg-slate-200 rounded-2xl flex items-center justify-center'>
                  <p className='text-slate-500'>Sin imágenes disponibles</p>
                </div>
              )}
            </motion.div>

            {/* Property Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className='bg-white rounded-2xl p-6 md:p-8 border border-slate-200 mb-8'
            >
              <div className='flex items-start justify-between mb-4'>
                <div className='flex-1'>
                  <div className='flex items-center space-x-2 mb-3'>
                    <Badge
                      className={`${
                        property.operation === 'SALE'
                          ? 'bg-primary'
                          : 'bg-secondary'
                      } text-white`}
                    >
                      {getOperationLabel(property.operation)}
                    </Badge>
                    {property.is_featured && (
                      <Badge className='bg-accent text-white'>Destacado</Badge>
                    )}
                  </div>
                  <h1
                    className='text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-2'
                    data-testid='property-title'
                  >
                    {property.title}
                  </h1>
                  <div className='flex items-center text-slate-600 mb-4'>
                    <MapPin className='w-5 h-5 mr-2 flex-shrink-0' />
                    <span className='text-lg'>
                      {property.neighborhood && `${property.neighborhood}, `}
                      {property.city}, {property.department}
                    </span>
                  </div>
                </div>
                <div className='flex space-x-2 ml-4'>
                  <Button
                    size='icon'
                    variant='outline'
                    onClick={() => toast.success('Añadido a favoritos')}
                    data-testid='favorite-button'
                  >
                    <Heart className='w-5 h-5' />
                  </Button>
                  <Button
                    size='icon'
                    variant='outline'
                    onClick={() => toast.success('Enlace copiado')}
                    data-testid='share-button'
                  >
                    <Share2 className='w-5 h-5' />
                  </Button>
                </div>
              </div>

              <div className='mb-6'>
                <p
                  className='text-4xl md:text-5xl font-heading font-bold text-primary'
                  data-testid='property-price'
                >
                  {formatPrice(property.price)}
                </p>
                {property.operation === 'RENT' && (
                  <p className='text-slate-600 font-medium'>por mes</p>
                )}
              </div>

              {/* Features Grid */}
              <div className='grid grid-cols-2 md:grid-cols-3 gap-4 mb-6'>
                {features.map((feature, index) => {
                  const Icon = feature.icon;
                  return (
                    <div key={index} className='bg-slate-50 rounded-xl p-4'>
                      <Icon className='w-5 h-5 text-primary mb-2' />
                      <p className='text-sm text-slate-600 mb-1'>
                        {feature.label}
                      </p>
                      <p className='font-semibold text-slate-900'>
                        {feature.value}
                      </p>
                    </div>
                  );
                })}
              </div>

              {/* Description */}
              <div>
                <h2 className='text-xl font-heading font-bold text-slate-900 mb-3'>
                  Descripción
                </h2>
                <p
                  className='text-slate-700 leading-relaxed'
                  data-testid='property-description'
                >
                  {property.description}
                </p>
              </div>

              {/* Address */}
              {property.address_text && (
                <div className='mt-6 pt-6 border-t border-slate-200'>
                  <h2 className='text-xl font-heading font-bold text-slate-900 mb-2'>
                    Dirección
                  </h2>
                  <p className='text-slate-700'>{property.address_text}</p>
                </div>
              )}

              {/* Map Placeholder */}
              <div className='mt-6 pt-6 border-t border-slate-200'>
                <h2 className='text-xl font-heading font-bold text-slate-900 mb-4'>
                  Ubicación
                </h2>
                <div className='bg-slate-100 rounded-xl h-64 flex items-center justify-center border border-slate-200'>
                  <div className='text-center'>
                    <MapPin className='w-12 h-12 text-slate-400 mx-auto mb-2' />
                    <p className='text-slate-600'>
                      {property.city}, {property.department}
                    </p>
                    <p className='text-sm text-slate-500 mt-2'>
                      Mapa interactivo próximamente
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Sidebar - Contact */}
          <div className='lg:col-span-1'>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className='bg-white rounded-2xl p-6 border border-slate-200 sticky top-24'
              data-testid='contact-card'
            >
              <h2 className='text-2xl font-heading font-bold text-slate-900 mb-4'>
                Contactar
              </h2>
              <p className='text-slate-600 mb-6'>
                ¿Interesado en esta propiedad? Contáctanos para más información.
              </p>

              <div className='space-y-3'>
                <Button
                  className='w-full bg-accent hover:bg-accent-hover h-12 text-base font-semibold'
                  onClick={() => handleContact('WhatsApp')}
                  data-testid='contact-whatsapp'
                >
                  <Phone className='w-5 h-5 mr-2' />
                  WhatsApp
                </Button>
                <Button
                  variant='outline'
                  className='w-full h-12 text-base font-semibold'
                  onClick={() => handleContact('Teléfono')}
                  data-testid='contact-phone'
                >
                  <Phone className='w-5 h-5 mr-2' />
                  Llamar
                </Button>
                <Button
                  variant='outline'
                  className='w-full h-12 text-base font-semibold'
                  onClick={() => handleContact('Email')}
                  data-testid='contact-email'
                >
                  <Mail className='w-5 h-5 mr-2' />
                  Email
                </Button>
              </div>

              <div className='mt-6 pt-6 border-t border-slate-200'>
                <p className='text-sm text-slate-500 text-center'>
                  ID de propiedad:{' '}
                  <span className='font-mono text-slate-700'>
                    {property.id.substring(0, 8)}
                  </span>
                </p>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Related Properties */}
        {relatedProperties.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className='mt-16'
            data-testid='related-properties'
          >
            <h2 className='text-3xl font-heading font-bold text-slate-900 mb-8'>
              Propiedades Similares
            </h2>
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
              {relatedProperties.map((prop) => (
                <PropertyCard key={prop.id} property={prop} />
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default PropertyDetailPage;
